import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceChargeService } from '@/services/compensation/serviceChargeService';
import { serviceChargeKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { ServiceChargePeriodInput, ServiceChargeWeightInput, ServiceChargeEmployeeAssignment, ServiceChargeDistributionMode } from '@/services/compensation/serviceChargeService';

export const useServiceChargeMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createPeriod = useMutation({
    mutationFn: ({ period, weights }: { period: ServiceChargePeriodInput; weights: ServiceChargeWeightInput[] }) =>
      serviceChargeService.createPeriod(period, weights),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periods() });
      toast({
        title: 'Period created',
        description: 'Service charge period created successfully',
      });
    },
    onError: (error: any) => {
      console.error('Failed to create period:', error);
      
      let errorMessage = 'Failed to create service charge period';
      
      // Parse Supabase constraint violation errors
      if (error?.code === '23505') {
        if (error?.message?.includes('unique_period_per_company')) {
          errorMessage = 'A service charge period already exists for these dates. Please choose different dates or edit the existing period.';
        } else if (error?.message?.includes('unique_weight_label')) {
          errorMessage = 'Duplicate weight category labels detected. Each weight category must have a unique label within the period.';
        } else {
          errorMessage = 'A duplicate entry was detected. Please check your inputs.';
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Cannot Create Period',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const updatePeriod = useMutation({
    mutationFn: ({ periodId, updates }: { periodId: string; updates: Partial<ServiceChargePeriodInput> }) =>
      serviceChargeService.updatePeriod(periodId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periods() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.period(variables.periodId) });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periodWithWeights(variables.periodId) });
      toast({
        title: 'Period updated',
        description: 'Service charge period updated successfully',
      });
    },
    onError: (error: any) => {
      console.error('Failed to update period:', error);
      
      let errorMessage = 'Failed to update service charge period';
      
      if (error?.code === '23505') {
        if (error?.message?.includes('unique_period_per_company')) {
          errorMessage = 'Another period already exists for these dates. Please choose different dates.';
        } else {
          errorMessage = 'A duplicate entry was detected. Please check your inputs.';
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Cannot Update Period',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const deletePeriod = useMutation({
    mutationFn: (periodId: string) => serviceChargeService.deletePeriod(periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periods() });
      toast({
        title: 'Period deleted',
        description: 'Service charge period deleted successfully',
      });
    },
    onError: (error) => {
      console.error('Failed to delete period:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete service charge period',
        variant: 'destructive',
      });
    },
  });

  const updatePeriodWeights = useMutation({
    mutationFn: ({ periodId, weights, distributionMode }: { periodId: string; weights: ServiceChargeWeightInput[]; distributionMode?: ServiceChargeDistributionMode }) =>
      serviceChargeService.updatePeriodWeights(periodId, weights, distributionMode ?? 'weighted'),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periods() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periodWithWeights(variables.periodId) });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.assignments(variables.periodId) });

      const description = result.deletedAssignmentCount > 0
        ? `Weight categories updated. ${result.deletedAssignmentCount} employee assignment(s) were removed due to deleted categories.`
        : 'Weight categories updated successfully';

      toast({
        title: 'Weights updated',
        description,
        variant: result.deletedAssignmentCount > 0 ? 'destructive' : 'default',
      });
    },
    onError: (error: any) => {
      console.error('Failed to update weights:', error);

      let errorMessage = 'Failed to update weight categories';
      if (error?.code === '23505' && error?.message?.includes('unique_weight_label')) {
        errorMessage = 'Duplicate weight category labels detected. Each must be unique within the period.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: 'Cannot Update Weights',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const assignEmployeeWeights = useMutation({
    mutationFn: ({ periodId, assignments, removedProfileIds }: { periodId: string; assignments: ServiceChargeEmployeeAssignment[]; removedProfileIds?: string[] }) =>
      serviceChargeService.assignEmployeeWeights(periodId, assignments, removedProfileIds ?? []),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.assignments(variables.periodId) });
      queryClient.invalidateQueries({ queryKey: [...serviceChargeKeys.all, 'distribution'] });
      toast({
        title: 'Weights assigned',
        description: 'Employee weights assigned successfully',
      });
    },
    onError: (error) => {
      console.error('Failed to assign weights:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign employee weights',
        variant: 'destructive',
      });
    },
  });

  const copyWeightsFromPeriod = useMutation({
    mutationFn: ({ sourcePeriodId, targetPeriodId }: { sourcePeriodId: string; targetPeriodId: string }) =>
      serviceChargeService.copyWeightsFromPeriod(sourcePeriodId, targetPeriodId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.assignments(variables.targetPeriodId) });
      queryClient.invalidateQueries({ queryKey: [...serviceChargeKeys.all, 'distribution'] });
      toast({
        title: 'Weights copied',
        description: 'Employee weights copied successfully',
      });
    },
    onError: (error) => {
      console.error('Failed to copy weights:', error);
      toast({
        title: 'Error',
        description: 'Failed to copy employee weights',
        variant: 'destructive',
      });
    },
  });

  const activatePeriod = useMutation({
    mutationFn: async (periodId: string) => {
      // First, deactivate any currently-active periods for this company
      // This is defense-in-depth; the DB trigger also enforces single-active
      const { data: targetPeriod, error: fetchError } = await supabase
        .from('service_charge_periods')
        .select('company_id')
        .eq('id', periodId)
        .single();
      
      if (fetchError) throw fetchError;

      // Set all active periods for this company back to draft
      const { error: deactivateError } = await supabase
        .from('service_charge_periods')
        .update({ status: 'draft' })
        .eq('company_id', targetPeriod.company_id)
        .eq('status', 'active')
        .neq('id', periodId);
      
      if (deactivateError) throw deactivateError;

      // Now activate the target period
      const { data, error } = await supabase
        .from('service_charge_periods')
        .update({ status: 'active' })
        .eq('id', periodId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, periodId) => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periods() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.period(periodId) });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periodWithWeights(periodId) });
      toast({
        title: 'Period activated',
        description: 'Service charge period is now active and will be included in payroll calculations',
      });
    },
    onError: (error: any) => {
      console.error('Failed to activate period:', error);
      
      let errorMessage = 'Failed to activate service charge period';
      if (error?.message?.includes('already active')) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const deactivatePeriod = useMutation({
    mutationFn: async (periodId: string) => {
      // Only allow deactivating active periods (not finalized)
      const { data, error } = await supabase
        .from('service_charge_periods')
        .update({ status: 'draft' })
        .eq('id', periodId)
        .eq('status', 'active') // Guard: only active → draft
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Period is not in active status and cannot be deactivated.');
      return data;
    },
    onSuccess: (_, periodId) => {
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periods() });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.period(periodId) });
      queryClient.invalidateQueries({ queryKey: serviceChargeKeys.periodWithWeights(periodId) });
      toast({
        title: 'Period deactivated',
        description: 'Service charge period has been set back to draft. You can now edit or delete it.',
      });
    },
    onError: (error) => {
      console.error('Failed to deactivate period:', error);
      toast({
        title: 'Error',
        description: 'Failed to deactivate service charge period',
        variant: 'destructive',
      });
    },
  });

  return {
    createPeriod,
    updatePeriod,
    updatePeriodWeights,
    deletePeriod,
    assignEmployeeWeights,
    copyWeightsFromPeriod,
    activatePeriod,
    deactivatePeriod,
  };
};
